import { Map as YMap } from 'yjs';
import Big from 'big.js';
import { calcPositionBetween } from 'js/utils/big';
import { standardCollator, randomBase32String } from 'js/utils/string';
import { buildPositionMap } from '../../common/utils';
import { getElementPage, moveAfter, moveBefore } from './utils';
import { deleteAnswerSetIfUnused } from './answer-set-actions';

export function updatePageFields(doc, pageGuid, updates) {
  const page = doc.getMap('pages').get(pageGuid);
  if (!page) {
    return;
  }

  doc.transact(() => {
    Object.entries(updates).forEach(([key, value]) => {
      page.set(key, value);
    });
  });
}

export function deleteCapabilityElement(doc, pageGuid, questionGuid) {
  const page = doc.getMap('pages').get(pageGuid);

  if (!page) {
    return;
  }

  page.get('capabilityElementPositions').delete(questionGuid);
}

export function moveCapabilityElementBefore(
  doc,
  pageGuid,
  elementGuid,
  beforeElementGuid
) {
  moveCapabilityElement(
    doc,
    pageGuid,
    elementGuid,
    beforeElementGuid,
    'before'
  );
}

export function moveCapabilityElementAfter(
  doc,
  pageGuid,
  elementGuid,
  afterElementGuid
) {
  moveCapabilityElement(doc, pageGuid, elementGuid, afterElementGuid, 'after');
}

export function addCapabilityElementToPage(doc, elementGuid, pageGuid) {
  const elementPositions = doc
    .getMap('pages')
    .get(pageGuid)
    .get('capabilityElementPositions');
  const lastPosition = Object.entries(elementPositions.toJSON()).reduce(
    (acc, [key, value]) => (elementGuid !== key && acc < value ? value : acc),
    '0.5'
  );

  elementPositions.set(
    elementGuid,
    calcPositionBetween(Big(lastPosition), Big(1)).toString()
  );
}

function moveCapabilityElement(
  doc,
  pageGuid,
  elementGuid,
  targetGuid,
  direction
) {
  if (elementGuid === targetGuid) {
    return;
  }

  const { sortFn, defaultMin, defaultMax } =
    direction === 'before'
      ? {
          sortFn: ([, a], [, b]) => -standardCollator.compare(a, b),
          defaultMin: 1,
          defaultMax: 0,
        }
      : {
          sortFn: ([, a], [, b]) => standardCollator.compare(a, b),
          defaultMin: 0,
          defaultMax: 1,
        };

  const capabilityElementPositions = doc
    .getMap('pages')
    .get(pageGuid)
    .get('capabilityElementPositions');
  const positions = capabilityElementPositions.toJSON();

  const sortedPositions = Object.entries(positions).sort(sortFn);
  let targetElementPosition = null,
    followingElementPosition = null;

  for (const [elementId, elementPosition] of sortedPositions) {
    if (elementId === targetGuid) {
      targetElementPosition = [elementId, elementPosition];
    } else if (targetElementPosition != null) {
      followingElementPosition = [elementId, elementPosition];
      break;
    }
  }

  // it's already in the correct position
  if (elementGuid === followingElementPosition?.[0]) {
    return;
  }

  const newPosition = calcPositionBetween(
    Big(targetElementPosition?.[1] ?? defaultMin),
    Big(followingElementPosition?.[1] ?? defaultMax)
  );

  capabilityElementPositions.set(elementGuid, newPosition.toString());
}

export function chooseCapabilitySet(doc, pageGuid, capabilitySet) {
  const page = doc.getMap('pages').get(pageGuid);
  page.set('capabilitySetId', capabilitySet.id);
}

export function togglePageCapabilitySelection(
  doc,
  pageGuid,
  capabilityGuid,
  newState
) {
  const page = doc.getMap('pages').get(pageGuid);
  const selectedCapabilities = page.get('selectedCapabilities');
  newState = newState ?? !selectedCapabilities.has(capabilityGuid);

  if (newState) {
    const capabilitySetId = doc
      .getMap('capabilityCache')
      .get(capabilityGuid)
      .get('capability_set_id');
    if (capabilitySetId) {
      selectedCapabilities.set(capabilityGuid, capabilitySetId);
    }
  } else {
    selectedCapabilities.delete(capabilityGuid);
  }
}

export function addPage(doc, pageType, pageProps = {}) {
  switch (pageType) {
    case 'standard-questions':
      return addStandardQuestionsPage(doc, pageProps);
    case 'capability-questions':
    default:
      return addCapabilityQuestionsPage(doc, pageProps);
  }
}

function getSortedPages(docPages) {
  return Object.values(docPages.toJSON() ?? {}).sort(
    (a, b) =>
      standardCollator.compare(
        a?.position?.assessmentPages?.p ?? '0.5',
        b?.position?.assessmentPages?.p ?? '0.5'
      ) || standardCollator.compare(a.guid, b.guid)
  );
}

function nextPagePosition(sortedPages) {
  const lastPosition =
    sortedPages[sortedPages.length - 1]?.position?.assessmentPages?.p ?? '0.5';
  return calcPositionBetween(Big(lastPosition), Big(1)).toString();
}

function addStandardQuestionsPage(doc, pageProps) {
  const docPages = doc.getMap('pages');
  const newPosition = nextPagePosition(getSortedPages(docPages));

  const guid = `p-${randomBase32String(10)}`;
  const newPage = new YMap(
    Object.entries({
      guid,
      type: 'standard-questions',
      title: 'Additional questions',
      description: null,
      ...pageProps,
    })
  );

  newPage.set(
    'position',
    buildPositionMap(
      docPages,
      'assessmentPages',
      'assessmentDocument',
      newPosition
    )
  );
  docPages.set(guid, newPage);

  return guid;
}

function addCapabilityQuestionsPage(doc, pageProps) {
  // find the last-used capability set and capability elements
  const docPages = doc.getMap('pages');
  const sortedPages = getSortedPages(docPages);

  let i = sortedPages.length,
    capabilitySetId = null,
    capabilityElementPositions = null;
  while (
    i-- > 0 &&
    (capabilitySetId === null || capabilityElementPositions === null)
  ) {
    const page = sortedPages[i];
    if (page.type === 'capability-questions') {
      capabilitySetId = capabilitySetId ?? page.capabilitySetId;
      capabilityElementPositions =
        capabilityElementPositions ?? page.capabilityElementPositions;
    }
  }

  const newPosition = nextPagePosition(sortedPages);

  const guid = `p-${randomBase32String(10)}`;
  const newPage = new YMap(
    Object.entries({
      guid,
      type: 'capability-questions',
      title: 'Rate your business capabilities',
      description: null,
      capabilitySetId,
    })
  );

  const newCapabilityElementPositions =
    capabilityElementPositions &&
    Object.keys(capabilityElementPositions).length > 0
      ? capabilityElementPositions
      : {
          satisfaction: '0.3',
          importance: '0.6',
        };

  newPage.set(
    'capabilityElementPositions',
    new YMap(Object.entries(newCapabilityElementPositions))
  );
  newPage.set('elementPositions', new YMap());
  newPage.set(
    'position',
    buildPositionMap(
      docPages,
      'assessmentPages',
      'assessmentDocument',
      newPosition
    )
  );
  newPage.set('selectedCapabilities', new YMap());
  docPages.set(guid, newPage);
}

export function deletePage(doc, pageGuid) {
  const docPages = doc.getMap('pages');
  const elements = doc.getMap('elements');

  doc.transact(() => {
    // A standard page owns its questions; capability pages only reference theirs
    // (answer set ids are read first: a deleted element no longer has them)
    const owned = [...elements.entries()]
      .filter(([, e]) => getElementPage(e) === pageGuid)
      .map(([guid, e]) => [guid, e.get('answerSetId')]);
    owned.forEach(([guid]) => elements.delete(guid));
    owned.forEach(([, answerSetGuid]) =>
      deleteAnswerSetIfUnused(doc, answerSetGuid)
    );

    docPages.delete(pageGuid);
  });
}

export function movePageAfter(doc, sourcePageGuid, destinationPageGuid) {
  moveAfter(
    doc,
    'pages',
    'assessmentPages',
    sourcePageGuid,
    destinationPageGuid
  );
}

export function movePageBefore(doc, sourcePageGuid, destinationPageGuid) {
  moveBefore(
    doc,
    'pages',
    'assessmentPages',
    sourcePageGuid,
    destinationPageGuid
  );
}
